import { isEmpty } from 'lodash'
import moment from 'moment'
import * as yup from 'yup'

import agent from 'agent'
import { type ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { type FontAwesomeIconName, type LegacyIconName } from 'components/Icon'
import { ScenarioPassCriteria } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import { isCustomUrl } from 'components/scenarios/create/status-checks/third-party-presets'
import { isRunInActiveStage } from 'components/scenarios/utils'
import { isContinuousStatusCheckNode } from 'components/scenarios/utils/graph/nodes/typeguards'
import { getStatusRunUrl } from 'components/services/service-route-utils'
import { type SERVICE_SETTINGS_TAB_NAMES } from 'components/services/settings/constants'
import {
  getActiveDaysOffset,
  shiftScheduleDayByOffset
} from 'components/services/settings/service-settings-page-utils'
import toast from 'lib/toast'
import theme from 'theme'
import {
  type ScenarioRun,
  ScenarioRunStage,
  type ScenarioRunStatus
} from 'types/api/scenarios/runs/scenario-runs'
import {
  type ConcurrentNode,
  type GraphNode,
  type GraphNodeList,
  type ScenarioGraph,
  ScenarioLifecycle
} from 'types/api/scenarios/scenario'
import {
  type FailureFlagService,
  type InfraService,
  ProcessProvenance,
  type UserDefinedService
} from 'types/api/services/service'
import {
  type LoadTestNodeDetails,
  type LoadTestNodeStatus,
  LoadTestStatus,
  LoadTestType
} from 'types/load-test'
import {
  ScheduleDay,
  type ScheduleTriggerType,
  type ScheduleSettings
} from 'types/schedules/schedules'
import { naturalSortBy } from 'utils/list'
import { roundNumber } from 'utils/numbers'
import { messageFromResponseError, prettyEnumConstant } from 'utils/strings'
import { formatDate, formatDateTime, formatTime } from 'utils/time/date'
import { WEEK_DAYS } from 'utils/time/days'
import { ms } from 'utils/time/millisecond'

import type { UpdateServicePayload } from 'queries/services/types'
import type { PolicyEvaluation, PolicyResult } from 'types/api/policies/policy'
import type { ReliabilityReport } from 'types/api/policies/reliability-report'
import type {
  NextReliabilityTestRunQueueItem,
  ReliabilityTestRun
} from 'types/api/reliability-tests/runs'
import type { LoadGenerator } from 'types/api/services/load-generator'
import type { ServiceStatusCheck } from 'types/api/status-checks/status-check'
import type { ScheduleFormSettings } from 'types/forms'
import type { Time24Hour } from 'types/time'
import type { FetchFunc } from 'types/util'

export const SERVICE_TYPE_FAILURE_FLAG = 'Failure Flag'
export type ServiceType =
  | ProvenanceTargetType
  | typeof SERVICE_TYPE_FAILURE_FLAG

export const MAX_SERVICE_NAME_LENGTH = 64
export const MAX_STATUS_CHECK_NAME_LENGTH = 128
// See https://devblogs.microsoft.com/oldnewthing/?p=7873 for why 253 was chosen
// should match value in `DefineDependencyRequest.java`
export const MAX_DEPENDENCY_NAME_LENGTH = 253
export const POLICIES_PER_PAGE = 5

// Defines the scheduling window
export const SCHEDULE_WINDOW_MINIMUM_HOURS = 2
export const SCHEDULE_WINDOW_MAXIMUM_HOURS = 24

// Get full range of possible positions of the end time value relative to start time value in the test schedule window.
// For the default window max and min, this is an Array of values [2, 3, ... , 23, 24]
export const TEST_SCHEDULE_WINDOW_DURATIONS = [
  ...Array(SCHEDULE_WINDOW_MAXIMUM_HOURS - SCHEDULE_WINDOW_MINIMUM_HOURS).keys()
].map((e) => e + SCHEDULE_WINDOW_MINIMUM_HOURS)

export const SERVICE_AUTO_SCHEDULE_TRIGGER_TYPES_LABELS: Record<
  ScheduleTriggerType,
  string
> = {
  Always: 'All Reliability Tests',
  Passed: 'Any Reliability Tests that have passed at least once',
  RunOnce: 'Any Reliability Tests that have been run at least once'
}

export const SERVICE_AUTO_SCHEDULE_TRIGGER_TYPES_ORDERED: ScheduleTriggerType[] =
  ['Always', 'Passed', 'RunOnce']

export const SERVICE_AUTO_SCHEDULE_TRIGGER_TYPES =
  SERVICE_AUTO_SCHEDULE_TRIGGER_TYPES_ORDERED.map((value) => ({
    label: SERVICE_AUTO_SCHEDULE_TRIGGER_TYPES_LABELS[value],
    value
  }))

export enum TestStatus {
  PreparingFor = 'preparing for',
  Running = 'running',
  Passed = 'passed',
  Failed = 'failed',
  Expired = 'expired',
  Attempted = 'attempted',
  Halting = 'halting'
}

export type ServiceStatusPageHeaderStates = {
  [key in ReliabilityTestStatus]: {
    icon: FontAwesomeIconName | LegacyIconName
    iconColor: string
    testStatus: TestStatus
    testStatusColor: string
    message: (expiryDate?: string, isPassCriteriaInverted?: boolean) => string
  }
}

export type ServiceStatusPageHeaderStateValues = {
  icon: FontAwesomeIconName | LegacyIconName
  iconColor: string
  testStatus: TestStatus
  testStatusColor: string
  message: string
}

function getServiceStatusPageHeaderStatePassedMessage(
  expiryDate?: string,
  isPassCriteriaInverted?: boolean
) {
  const expirationMessage = expiryDate
    ? ` This test result will expire on ${expiryDate}`
    : ''
  return isPassCriteriaInverted
    ? `Your Health Checks observed unhealthy states and were triggered during this test. Because this was the expected result, it has passed.${expirationMessage}`
    : `Your Health Checks observed healthy criteria while Gremlin was testing your system.${expirationMessage}`
}

function getServiceStatusPageHeaderStateFailedMessage(
  _expiryDate?: string,
  isPassCriteriaInverted?: boolean
) {
  return isPassCriteriaInverted
    ? 'No Health Checks were triggered during this test. Because this was the expected result, it has failed.'
    : 'Your Health Checks observed unhealthy status codes while Gremlin was testing your system.'
}

// Dictionary object for evaluating and displaying different reliability test states
// TODO: Improve the run status logic to reduce the number of duplicate cases
export const SERVICE_STATUS_PAGE_HEADER_STATES: ServiceStatusPageHeaderStates =
  {
    SETUP: {
      icon: 'solidArrowsRotate',
      iconColor: theme.colors.purple[0],
      testStatus: TestStatus.PreparingFor,
      testStatusColor: theme.colors.purple[0],
      message: () => ''
    },
    ACTIVE: {
      icon: 'solidArrowsRotate',
      iconColor: theme.colors.purple[0],
      testStatus: TestStatus.Running,
      testStatusColor: theme.colors.purple[0],
      message: () => ''
    },
    NOTSTARTED: {
      icon: 'solidArrowsRotate',
      iconColor: theme.colors.purple[0],
      testStatus: TestStatus.Running,
      testStatusColor: theme.colors.purple[0],
      message: () => ''
    },
    PASSED: {
      icon: 'solidCircleCheck',
      iconColor: theme.colors.green[0],
      testStatus: TestStatus.Passed,
      testStatusColor: theme.colors.green[0],
      message: getServiceStatusPageHeaderStatePassedMessage
    },
    SUCCESSFUL: {
      icon: 'solidCircleCheck',
      iconColor: theme.colors.green[0],
      testStatus: TestStatus.Passed,
      testStatusColor: theme.colors.green[0],
      message: getServiceStatusPageHeaderStatePassedMessage
    },
    FAILED: {
      icon: 'solidCircleXmark',
      iconColor: theme.colors.red[0],
      testStatus: TestStatus.Failed,
      testStatusColor: theme.colors.red[0],
      message: getServiceStatusPageHeaderStateFailedMessage
    },
    EXPIRED: {
      icon: 'solidCircleMinus',
      iconColor: theme.colors.orange[0],
      testStatus: TestStatus.Expired,
      testStatusColor: theme.colors.orange[0],
      message: (expiryDate) =>
        `This test expired on ${expiryDate}. Run this test again to refresh the result.`
    },
    HALTING: {
      icon: 'solidArrowsRotate',
      iconColor: theme.colors.steel[1],
      testStatus: TestStatus.Halting,
      testStatusColor: theme.colors.steel[1],
      message: () => ''
    },
    HALTED: {
      icon: 'solidCircleMinus',
      iconColor: theme.colors.steel[1],
      testStatus: TestStatus.Attempted,
      testStatusColor: theme.colors.steel[1],
      message: () =>
        'There was a problem while Gremlin was testing. We recommend trying to run this test again.'
    },
    USERHALTED: {
      icon: 'solidCircleMinus',
      iconColor: theme.colors.steel[1],
      testStatus: TestStatus.Attempted,
      testStatusColor: theme.colors.steel[1],
      message: () =>
        'A user halted this test while it was in progress. Run this test again, without halting, to get results.'
    },
    HALTREQUESTED: {
      icon: 'solidCircleMinus',
      iconColor: theme.colors.steel[1],
      testStatus: TestStatus.Attempted,
      testStatusColor: theme.colors.steel[1],
      message: () =>
        'A user halted this test while it was in progress. Run this test again, without halting, to get results.'
    },
    UNSURE: {
      icon: 'solidCircleMinus',
      iconColor: theme.colors.steel[1],
      testStatus: TestStatus.Attempted,
      testStatusColor: theme.colors.steel[1],
      message: () => ''
    }
  }

export const serviceFormSchemaValidation = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required('Please enter the service name')
    .min(1, 'Service name cannot be empty')
    .matches(/\S+/g, 'Please enter the service name')
    .max(MAX_SERVICE_NAME_LENGTH),
  scheduleSettings: yup.object().shape({
    enabled: yup.boolean().required(),
    triggerType: yup
      .string()
      .oneOf(
        SERVICE_AUTO_SCHEDULE_TRIGGER_TYPES.map(
          (triggerType) => triggerType.value
        )
      )
      .when('enabled', {
        is: true,
        then: (schema) => schema.required('Trigger type is required')
      }),
    activeDays: yup.array(yup.mixed<ScheduleDay>()).when('enabled', {
      is: true,
      then: (schema) =>
        schema.required('At least one active day must be selected').min(1)
    }),
    start: yup.date().when('enabled', {
      is: true,
      then: (schema) => schema.required('Start time is required')
    }),
    end: yup
      .date()
      .when('enabled', {
        is: true,
        then: (schema) => schema.required('End time is required')
      })
      .test({
        name: 'isEndTimeInScheduleWindow',
        message: `End time must be between ${SCHEDULE_WINDOW_MINIMUM_HOURS} and ${SCHEDULE_WINDOW_MAXIMUM_HOURS} hours after start time`,
        test: function (value) {
          const diff = moment(value).diff(moment(this.parent.start), 'hours')

          return (
            diff > SCHEDULE_WINDOW_MINIMUM_HOURS ||
            diff < SCHEDULE_WINDOW_MAXIMUM_HOURS
          )
        }
      })
  })
})

export function getProvenanceByTargetType(targetType: ProvenanceTargetType) {
  if (targetType === 'Host') return ProcessProvenance.ProcessData
  if (targetType === 'Container') return ProcessProvenance.Container

  return ProcessProvenance.Kubernetes
}

/** With the introduction of Failure Flag services, we need to check beyond
 * ProvenanceTargetType to obtain the service type */
export function getServiceType(service: UserDefinedService): ServiceType {
  return service.targetingStrategy?.type ?? SERVICE_TYPE_FAILURE_FLAG
}

// to ensure that we only display the score digits exactly as we want to in the UI
export function filterReliabilityScore(score: number | string) {
  return roundNumber(Number(score), 2)
}

function findNodeInGraph(webhookId: string, branches: ScenarioGraph[]) {
  return branches
    .flatMap((b) => Object.values(b.nodes))
    .find((n) => n.id === webhookId)
}

export function getLoadGeneratorNodesFromGraphNodes(
  nodes?: GraphNodeList | undefined,
  loadGenerator?: LoadGenerator[]
): LoadTestNodeDetails[] {
  const { branches } = (nodes?.concurrentNode ?? {}) as ConcurrentNode

  if (!branches?.length || !loadGenerator?.length) {
    return []
  }

  return loadGenerator.map((l) => ({
    name: l.name,
    id: l.identifier,
    // I left `findNodeInGraph` in place because we don't know if the original implementation had an intention to support load generator outside a service test run
    triggerNode:
      findNodeInGraph(l.webhook_start_load_trigger_id, branches) ||
      nodes?.[l.webhook_start_load_trigger_id],
    loadStatusNode:
      findNodeInGraph(l.webhook_status_check_id, branches) ||
      nodes?.[l.webhook_status_check_id],
    stopNode:
      findNodeInGraph(l.webhook_stop_load_trigger_id, branches) ||
      nodes?.[l.webhook_stop_load_trigger_id]
  }))
}

export const computeLoadGeneratorNodesStatus = (
  loadGenerators: LoadTestNodeDetails[]
): LoadTestNodeStatus[] =>
  loadGenerators.map((l) => {
    const triggerStatus = computeLoadTestNodeStatus(
      LoadTestType.Start,
      l.triggerNode
    )
    const checkStatus = computeLoadTestNodeStatus(
      LoadTestType.Status,
      l.loadStatusNode
    )
    const stopStatus = computeLoadTestNodeStatus(LoadTestType.Stop, l.stopNode)

    const errorAtStart = triggerStatus === LoadTestStatus.Error
    const errorAtStatusCheck = checkStatus === LoadTestStatus.Error
    const errorAtStop = stopStatus === LoadTestStatus.Error

    const errorAt =
      (errorAtStart && LoadTestType.Start) ||
      (errorAtStatusCheck && LoadTestType.Status) ||
      (errorAtStop && LoadTestType.Stop) ||
      undefined

    return {
      loadGeneratorId: l.id,
      loadGeneratorName: l.name,
      status:
        stopStatus || checkStatus || triggerStatus || LoadTestStatus.NotStarted,
      errorAt: errorAt
    }
  })

const passedStatuses: Record<LoadTestType, LoadTestStatus> = {
  Start: LoadTestStatus.Triggered,
  Stop: LoadTestStatus.Stopped,
  Status: LoadTestStatus.Running
}

export function computeLoadTestNodeStatus(
  type: LoadTestType,
  node?: GraphNode
) {
  const isHalted = node?.state?.lifecycle === ScenarioLifecycle.Halted
  if (isHalted) {
    return LoadTestStatus.Halted
  }

  const passed = node?.state?.evaluation_results?.okStatusCodes?.passed
  if (passed === true) {
    return passedStatuses[type]
  }

  if (passed === false) {
    return LoadTestStatus.Error
  }
}

export type PolicyResultWithRunning = PolicyResult | 'Running'

export type PolicyResultCounts = Partial<{
  [key in PolicyResultWithRunning]: number
}>

export function getPolicyEvaluationStatus(
  policyEvaluation: PolicyEvaluation
): PolicyResult {
  if (!policyEvaluation?.evaluationTime) {
    return 'NEVER_RUN'
  }

  return policyEvaluation?.result
}

// TODO: Refactor this function to accommodate values from the PolicyEvaluation type
export function getReliabilityTestRunResultColor(resultState: string) {
  switch (resultState.toLowerCase()) {
    case 'passed':
      return 'success'
    case 'failed':
    case 'unsure':
      return 'error'
    case 'expired':
      return 'warning'
    case 'running':
      return 'secondary'
    default:
      return 'default'
  }
}

export function getPrettyRunStatus(
  status: PolicyResult,
  passCriteria = ScenarioPassCriteria.ALL_PASS
) {
  if (status === 'NEVER_RUN') {
    // to prevent a breaking API change, map this status to the `TO DO` string on the FE
    return 'To Do'
  }

  if (
    passCriteria === ScenarioPassCriteria.AT_LEAST_ONE_FAILED &&
    status === 'PASSED'
  ) {
    return 'Triggered'
  }

  return prettyEnumConstant(status)
}

export function getIconRunStatus(status: PolicyResult) {
  switch (status) {
    case 'PASSED':
      return 'solidCheck'
    case 'FAILED':
      return 'solidClose'
    case 'EXPIRED':
      return 'solidWarning'
    case 'NEVER_RUN':
    default:
      return 'solidMinus'
  }
}

export function getScheduleSettingsForPayload(
  scheduleSettings: ScheduleFormSettings
): UpdateServicePayload['scheduleSettings'] {
  const {
    activeDays,
    start,
    end,
    enabled,
    numOfRuns,
    scheduleType,
    timezone,
    triggerType
  } = scheduleSettings

  /*
   * Start & End times are stored in UTC, and have specificity by which they were easily manipulated
   * according to the inputs of the user. However, the active days have no such specificity, so we must
   * determine here if further adjustments are necessary based on the user inputs which may necessitate
   * these active days to carry over to the previous or next day when normalizing to UTC.
   */
  const daysDelta = getActiveDaysOffset(start, timezone, 'UTC')
  const triggers = activeDays?.length
    ? activeDays.map((day) => {
        return {
          // Shifts the active day by the calculated delta to ensure proper UTC representation.
          dayOfWeek: shiftScheduleDayByOffset(day, -daysDelta),
          scheduleWindows: [
            {
              start: moment.utc(start).format('HH:mm') as Time24Hour, // 24 hour UTC time
              end: moment.utc(end).format('HH:mm') as Time24Hour, // 24 hour UTC time
              numOfRuns: numOfRuns ?? 1 // Defaulting to 1 for Impala V1
            }
          ]
        }
      })
    : null

  return {
    enabled,
    scheduleType: scheduleType ?? 'Random', // Defaulting to 'Random' for Impala V1
    triggerType,
    triggers
  }
}

export function getInitialScheduleFormSettings(
  scheduleSettings: ScheduleSettings = { enabled: false }
): ScheduleFormSettings {
  const { enabled, triggers, triggerType } = scheduleSettings

  // Default active day to Monday to line up with default start time
  const activeDays = triggers?.length
    ? triggers.map((value) => {
        return value.dayOfWeek
      })
    : [ScheduleDay.Monday]

  const autoScheduleStart = moment().utc().hour(12).minute(0).day(1) // Initialize autoScheduling start time to 12pm UTC time with day set to Monday
  const autoScheduleEnd = moment(autoScheduleStart).add(4, 'hours') // Initialize autoScheduling end time to 4 hours past start time

  //generates initial form values
  const start = triggers?.length
    ? moment
        .utc(triggers[0]?.scheduleWindows[0]?.start, 'HH:mm')
        .day(WEEK_DAYS.indexOf(triggers[0]?.dayOfWeek ?? ScheduleDay.Monday))
    : autoScheduleStart

  const end = triggers?.length
    ? moment
        .utc(triggers[0]?.scheduleWindows[0]?.end, 'HH:mm')
        .day(WEEK_DAYS.indexOf(triggers[0]?.dayOfWeek ?? ScheduleDay.Monday))
    : autoScheduleEnd

  if (start.isAfter(end)) {
    end.add(1, 'day')
  }

  return {
    enabled,
    triggerType: triggerType ?? 'Passed',
    activeDays,
    start: start.format(),
    end: end.format(),
    timezone: 'UTC'
  }
}

export function getScheduleWindowText(schedule: ScheduleSettings) {
  if (schedule?.triggers && schedule.triggers.length < 1) {
    return ''
  }

  const days = {
    Su: 'Sun',
    M: 'Mon',
    T: 'Tue',
    W: 'Wed',
    Th: 'Thu',
    F: 'Fri',
    S: 'Sat'
  }

  const scheduledDailyTimeWindows = schedule?.triggers?.map((trigger) => {
    const timeWindows = trigger.scheduleWindows?.map((window) => {
      return `${moment.utc(window.start, 'HH:mm').format('ha')} - ${moment
        .utc(window.end, 'HH:mm')
        .format('ha')} (UTC)`
    })
    return `${days[trigger.dayOfWeek]}: ${timeWindows}`
  })

  return scheduledDailyTimeWindows?.join('\n')
}

export function isAutoSchedulingValid(values) {
  const { activeDays, enabled, start, end } = values

  if (enabled) {
    // Check that both times have been entered
    if (!start || !end) {
      return true
    }

    const startMoment = moment(start)
    const endMoment = moment(end)
    const timeWindow = endMoment.diff(startMoment, 'hours')

    // Check that the end time is within the defined time window for the scheduler
    if (
      timeWindow < SCHEDULE_WINDOW_MINIMUM_HOURS ||
      timeWindow > SCHEDULE_WINDOW_MAXIMUM_HOURS
    ) {
      return true
    }

    // Check that any days have been selected
    if (activeDays?.length < 1) {
      return true
    }
  }

  return false
}

// Any test run which includes these stages are considered 'active'
const activeStages = [ScenarioRunStage.SETUP, ScenarioRunStage.ACTIVE]

/*
 * Client-side filtering of all 'active' reliability tests runs are required for BE performance
 * reasons. Further filtering is applied to only include an active test run.
 */
export function getActiveReliabilityTestForService(
  data: ReliabilityTestRun[],
  serviceId: string
) {
  const rmRun = data.find((run) => run.serviceId === serviceId)
  const stage = rmRun?.run?.stage_info?.stage

  if (stage !== undefined && activeStages.includes(stage)) {
    return rmRun
  }
}

export const imminentRMTestTimeThreshold = ms.minutes(5)
export const FALLBACK_RELIABILITY_TEST_RUN_STATUS = 'UNSURE'

/**
 * Given the next Reliability test run in the queue, determine if the next test
 * is soon to be executed. Based on the 5 minute buffer which is placed between each run
 * within or between series.
 */
export function isNextReliabilityTestRunImminent(
  nextRun?: NextReliabilityTestRunQueueItem
) {
  if (!nextRun) return false

  const nextRunStartTime = new Date(nextRun.expectedTriggerTime)

  return nextRunStartTime.getTime() - Date.now() <= imminentRMTestTimeThreshold
}

export function isReliabilityTestExpired(expiry: number): boolean {
  const e_date = new Date(expiry)
  const now = new Date(Date.now())
  return now.getTime() - e_date.getTime() >= 0
}

export type ReliabilityTestStatus =
  | Uppercase<ScenarioRunStatus>
  | Uppercase<ScenarioLifecycle>
  | 'SETUP'
  | 'EXPIRED'
  | 'HALTING'

/**
 * Check if run has passed independent of it being expired.
 * This is useful for checking against failed steps so that we can display more information.
 * Generally, use getReliabilityTestStatus() for detailed and accurate status.
 */
export function hasRunPassed(latestRun: ScenarioRun) {
  return latestRun?.results?.status === 'Passed'
}

export function getReliabilityTestStatus(
  policyState?: PolicyEvaluation,
  latestRun?: ScenarioRun
): ReliabilityTestStatus {
  const latestRunStage = (
    latestRun?.stage_info?.stage ?? latestRun?.stageInfo?.stage
  )?.toUpperCase()
  const latestRunResultsStatus = latestRun?.results?.status?.toUpperCase()

  if (latestRun && isRunInActiveStage(latestRun)) {
    return latestRunStage?.toUpperCase() as ReliabilityTestStatus
  }

  // Evaluate the run results object
  if (latestRunResultsStatus === 'PASSED') {
    const { evaluationTime, staleness } = {
      evaluationTime: -1,
      staleness: -1,
      ...policyState
    }

    const expiry = evaluationTime + staleness

    return expiry >= 0 && isReliabilityTestExpired(expiry)
      ? 'EXPIRED'
      : latestRunResultsStatus
  }

  if (latestRunResultsStatus === 'FAILED') {
    return latestRunResultsStatus
  }

  // NOTE: Zero confidence this is grabbing the latest node in a graph with concurrent nodes
  const lastNodeStatus = latestRun?.graph?.nodes_recursive
    ?.find(
      (node) =>
        node?.state?.end_time === undefined &&
        !isContinuousStatusCheckNode(node)
    )
    ?.state?.lifecycle?.toUpperCase()

  if (
    lastNodeStatus !== 'USERHALTED' &&
    latestRunResultsStatus === 'UNSURE' // This indicates the test has been halted
  ) {
    return 'HALTED'
  }

  return (
    (lastNodeStatus as ReliabilityTestStatus) ??
    FALLBACK_RELIABILITY_TEST_RUN_STATUS
  )
}

export function hasTestBeenRun(policyEvaluation: PolicyEvaluation) {
  const runStatus = getPolicyEvaluationStatus(policyEvaluation)
  return runStatus !== 'NEVER_RUN'
}

export function isTestScheduled(
  service: UserDefinedService,
  reliabilityTestId: string,
  dependencyId?: string | null
) {
  if (dependencyId) {
    return (
      service?.schedulableTests?.find(
        (t) =>
          t.reliabilityTestId === reliabilityTestId &&
          t.dependencyId === dependencyId
      ) !== undefined
    )
  } else {
    return (
      service?.schedulableTests?.find(
        (t) => t.reliabilityTestId === reliabilityTestId && !t.dependencyId
      ) !== undefined
    )
  }
}

export async function runAllTests(
  serviceId: string,
  currentTeamId: string,
  onSuccess: FetchFunc = async () => {}
) {
  try {
    await agent.post(
      `/services/${serviceId}/baseline?teamId=${currentTeamId}`,
      {
        startBaselineRequest: {
          tests: []
        }
      }
    )

    toast({
      type: 'success',
      text: 'Running All Tests'
    })

    onSuccess()
  } catch (e) {
    toast({
      type: 'error',
      text: messageFromResponseError(
        e,
        'There was an issue running all tests on this service. Please try again later.'
      )
    })
  }
}

export function getIsFailureFlagService(
  service: UserDefinedService
): service is FailureFlagService {
  return 'applicationSelector' in service
}

export function getIsInfraService(
  service: UserDefinedService
): service is InfraService {
  return 'targetingStrategy' in service
}

/**
 * Builds an optional record of query param keys such that the two inputs
 * remain mutually exclusive.
 */
export function getOptionalServiceQueryKeyParams(
  dependencyId: string | null | undefined,
  failureFlagName: string | null | undefined
): { dependencyId: string } | { failureFlagName: string } | undefined {
  if (dependencyId) return { dependencyId }
  if (failureFlagName) return { failureFlagName }
}

export function getReliabilityScoreData(reliabilityReport: ReliabilityReport) {
  return reliabilityReport?.reliability
    ? naturalSortBy(
        Object.entries(reliabilityReport.reliability),
        (c) => c[1].rank
      ).map(([key, value]) => ({
        gremlin: key,
        count: value.score
      }))
    : []
}

export function getJiraIssueSummary(
  service: UserDefinedService,
  run: ScenarioRun,
  reliabilityId: string
) {
  return [
    'Gremlin',
    `${service.name}`,
    reliabilityId,
    `${run && formatDate(run.created_at)}`
  ].join('-')
}

export function getJiraIssueDescription(
  service: UserDefinedService,
  run: ScenarioRun,
  reliabilityId: string,
  dependencyId?: string
) {
  return [
    `*Service Name:*\n${service.name}`,
    `*Reliability Test Run:*\n${window.location.origin}${getStatusRunUrl({
      dependencyId,
      runNumber: run?.run_number,
      serviceId: service.serviceId,
      testId: reliabilityId
    })}`,
    `*Last Run:*\n${run && formatDateTime(run.created_at)}`,
    `*Result:*\n${run?.results?.status}`
  ].join('\n\n')
}

/**
 * Function will sanitize the policy name passed in to remove redundant 'Test' strings from the view
 * @param name
 */
export function getDisplayPolicyName(name?: string): string {
  const nameRegex = /Test$/g

  if (!name) {
    return ''
  }

  if (nameRegex.exec(name)) {
    return name
  }

  return `${name} Test`
}

export const isTargetParamsEmpty = (targetParams) =>
  isEmpty(targetParams) ||
  Object.values(targetParams).every((paramValues) => {
    if (paramValues instanceof Set) {
      return paramValues.size === 0
    } else {
      return !paramValues
    }
  })

export function getPolicyStateForTest({
  dependencyId,
  failureFlagName,
  reliabilityReport,
  reliabilityTestId = ''
}: {
  dependencyId?: string
  failureFlagName?: string
  reliabilityReport?: ReliabilityReport
  reliabilityTestId?: string
}) {
  const policyStatesForTest = Object.entries(
    reliabilityReport?.reliability ?? {}
  )
    .flatMap(([, category]) => category.policyStates)
    .filter((s) => s.reliabilityTestId === reliabilityTestId)

  if (dependencyId) {
    return policyStatesForTest.find((s) => s.dependencyId === dependencyId)
  }

  if (failureFlagName) {
    return policyStatesForTest.find(
      (s) => s.failureFlagName === failureFlagName
    )
  }

  // TODO pretty sure there should never be more than one, but we may want to validate this and note it here
  return policyStatesForTest[0]
}

export function convertCentsToDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

export function isServiceSetupComplete(service?: UserDefinedService) {
  if (!service) return false

  return (
    Boolean(service.statusCheckIds?.length) ||
    Boolean(service.intelligentHealthCheckTargetIds?.length) ||
    service.enabledGremlinAwsStatusCheck
  )
}

export function hasServiceRunATest(reliabilityReport?: ReliabilityReport) {
  if (!reliabilityReport?.reliability) {
    return false
  }

  const hasRun = Object.values(reliabilityReport.reliability).some((category) =>
    category?.policyStates?.some((policyState) => hasTestBeenRun(policyState))
  )

  return hasRun
}

export function getPolicyEvaluationRunDetails(
  evaluation: PolicyEvaluation,
  neverTestedText = 'Never Tested'
) {
  const runStatus = getPolicyEvaluationStatus(evaluation)
  const hasTestRun = runStatus !== 'NEVER_RUN'
  const lastTested =
    hasTestRun && evaluation
      ? `${formatDate(evaluation?.evaluationTime)} ${formatTime(
          evaluation?.evaluationTime
        )}`
      : neverTestedText

  return {
    runStatus,
    hasTestRun,
    lastTested
  }
}

/**
 * Returns true if the service status check is a custom status check
 */
export function isCustomServiceStatusCheck(
  serviceStatusCheck: ServiceStatusCheck
) {
  return isCustomUrl(
    serviceStatusCheck.integration.url,
    serviceStatusCheck.integration.observabilityToolType
  )
}

export function getRunReliabilityTestButtonDisabledMessage(
  isServiceRunningATest?: boolean,
  isSetupComplete?: boolean
): string {
  if (isServiceRunningATest) {
    return 'A reliability test is in progress. Wait for it to finish before starting another reliability test.'
  } else if (!isSetupComplete) {
    return 'To run Reliability Tests, add a Health Check'
  } else {
    return ''
  }
}

export function getTestRunStartTimeFormat(time: Date | string) {
  const date = new Date(time)

  if (isNaN(date.valueOf())) {
    return 'Unknown Start Time'
  }

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short'
  })
}

export function getServiceSettingsUrl(
  serviceId: string,
  settingTab?: typeof SERVICE_SETTINGS_TAB_NAMES[number]
) {
  return `/services/${serviceId}/settings/${settingTab || ''}`
}
